// src/Components/ViewInfo/ViewInfoFullPage.js

import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useNavigate, useLocation } from 'react-router-dom';

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Firebase/firebase';
import { MenuDownload } from '../../assets/icons/CustomIcons';

const MediaElement = ({ slide }) => {
  const resolvedUrl = slide.url || slide.img || '';

  if (!resolvedUrl) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Typography color='text.secondary'>No Media Available</Typography>
      </Box>
    );
  }

  const searchStr = String(
    resolvedUrl + (slide.fileName || slide.title || ''),
  ).toLowerCase();
  const isVideo = slide.type === 'video' || searchStr.match(/\.(mp4|mov|webm)/i);
  const isAudio = slide.type === 'audio' || searchStr.match(/\.(mp3|wav|ogg|m4a|aac)/i);

  if (isVideo) {
    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <video
          src={resolvedUrl}
          playsInline
          controls
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>
    );
  }

  if (isAudio) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#e0e0e0',
        }}>
        <audio controls src={resolvedUrl} style={{ width: '80%' }} />
      </Box>
    );
  }

  return (
    <img
      src={resolvedUrl}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      alt={slide.markerName || 'Media'}
    />
  );
};

const InfoBlock = ({ label, value, isHtml }) => {
  if (value === null || value === undefined) return null;

  let displayValue = value;

  if (Array.isArray(value)) {
    displayValue = value
      .map((v) =>
        typeof v === 'object' && v !== null ? v.label || v.name || v.title || '' : v,
      )
      .filter(Boolean)
      .join(', ');
  } else if (typeof value === 'object' && value !== null) {
    displayValue = value.label || value.name || value.title || '';
  }

  const stringValue = String(displayValue);

  if (
    stringValue.trim() === '' ||
    stringValue === '<p></p>' ||
    stringValue === '[object Object]'
  )
    return null;

  return (
    <React.Fragment>
      <Typography
        sx={{
          fontWeight: 'bold',
          color: '#00008B',
          fontSize: '0.65rem',
          textTransform: 'uppercase',
          mt: '2px',
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        }}>
        {label}
      </Typography>

      {isHtml ? (
        <Box
          sx={{
            typography: 'body1',
            color: '#333',
            fontSize: '0.9rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            textTransform: 'none !important', // AMENDED: Prevent global theme uppercase leakage
            '& p': { mt: 0, mb: '6pt', lineHeight: 1.2 }, // AMENDED: Sync with RTE margins
            '& p:last-of-type': { mb: 0 },
            '& ul, & ol': { paddingLeft: '1.5rem', mt: 0, mb: 0 },
            '& li': { marginTop: 0, marginBottom: '6pt', lineHeight: 1.1 },
            '& li > p': { margin: '0 !important' },
            '& a': { color: '#1976d2', textDecoration: 'none' },
          }}
          dangerouslySetInnerHTML={{ __html: stringValue }}
        />
      ) : (
        <Typography
          sx={{
            color: '#333',
            fontSize: '0.9rem',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          }}>
          {stringValue}
        </Typography>
      )}
    </React.Fragment>
  );
};

export default function ViewInfoFullPage({ logoUrl }) {
  const navigate = useNavigate();
  const location = useLocation();

  const { selectedMarker } = location.state || {};

  // --- DB Live State (THE FIX) ---
  const [liveMarker, setLiveMarker] = useState(selectedMarker);

  const [resolvedCategory, setResolvedCategory] = useState('');
  const [resolvedRegion, setResolvedRegion] = useState('');
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());

  useEffect(() => {
    if (!location.state) {
      navigate('/', { replace: true });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    let isMounted = true;
    if (selectedMarker) setLiveMarker(selectedMarker);

    const fetchRelationalData = async () => {
      if (!selectedMarker) return;

      try {
        const markerId = selectedMarker.markerGroupID || selectedMarker.id;
        if (!markerId) return;

        const markerRef = doc(db, 'markerGroups', markerId);
        const markerSnap = await getDoc(markerRef);

        if (markerSnap.exists()) {
          const markerData = markerSnap.data();

          if (isMounted) {
            // Actively overwrite local state with the latest photos and docs
            setLiveMarker((prev) => ({
              ...prev,
              ...markerData,
              photos: markerData.photos || [],
              documents: markerData.documents || [],
            }));
          }

          const extractId = (val) => {
            if (Array.isArray(val)) return val[0];
            if (typeof val === 'object' && val !== null) return val.value || val.id;
            return val;
          };

          const catId = extractId(
            markerData.categoryId || markerData.categoryID || markerData.category,
          );
          const regId = extractId(
            markerData.regionId || markerData.regionID || markerData.region,
          );

          if (catId && typeof catId === 'string') {
            const catRef = doc(db, 'categories', catId);
            const catSnap = await getDoc(catRef);
            if (catSnap.exists() && isMounted) {
              setResolvedCategory(catSnap.data().name);
            }
          }

          if (regId && typeof regId === 'string') {
            const regRef = doc(db, 'region', regId);
            const regSnap = await getDoc(regRef);
            if (regSnap.exists() && isMounted) {
              setResolvedRegion(regSnap.data().name);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching relational data:', error);
      }
    };

    fetchRelationalData();

    return () => {
      isMounted = false;
    };
  }, [selectedMarker]);

  const handleCloseWindow = () => {
    navigate(-1);
  };

  const docs = liveMarker?.documents || [];
  const isAllDocsSelected = docs.length > 0 && selectedDocIds.size === docs.length;
  const isDocsIndeterminate =
    selectedDocIds.size > 0 && selectedDocIds.size < docs.length;

  const handleSelectAllDocs = (event) => {
    if (event.target.checked) {
      setSelectedDocIds(new Set(docs.map((item) => item.id)));
    } else {
      setSelectedDocIds(new Set());
    }
  };

  const handleDocRowClick = (event, id) => {
    event.stopPropagation();
    const newSelected = new Set(selectedDocIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedDocIds(newSelected);
  };

  const downloadFile = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleDownload = async () => {
    if (selectedDocIds.size === 0) return;
    const docsToDownload = docs.filter((doc) => selectedDocIds.has(doc.id));
    for (const doc of docsToDownload) {
      if (doc.url) await downloadFile(doc.url, doc.fileName);
    }
  };

  if (!location.state || !liveMarker) return null;

  const photos = liveMarker?.photos || [];
  const displaySlides = photos.map((p, i) => {
    const resolvedUrl = p.url || p.img || '';
    let resolvedType = p.type || 'image';

    if (resolvedType === 'image') {
      const searchString = String(resolvedUrl + (p.fileName || '')).toLowerCase();
      if (searchString.match(/\.(mp4|mov|webm)/i)) resolvedType = 'video';
      if (searchString.match(/\.(mp3|wav|ogg|m4a|aac)/i)) resolvedType = 'audio';
    }

    return {
      id: p.id || i,
      url: resolvedUrl,
      description: p.description,
      type: resolvedType,
      fileName: p.fileName,
      markerName: liveMarker.title || liveMarker.name,
      originalIndex: i,
    };
  });

  let showcaseValue = liveMarker.showcase ?? liveMarker.Showcase;
  if (showcaseValue === true) showcaseValue = 'Yes';
  else if (showcaseValue === false) showcaseValue = 'No';

  let hilightValue = liveMarker.hilight ?? liveMarker.Hilight;
  if (hilightValue === true) hilightValue = 'Yes';
  else if (hilightValue === false || hilightValue === undefined) hilightValue = 'No';

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}>
      <Box
        sx={{
          width: '100%',
          height: '50px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#fff',
          px: 3,
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: 10,
        }}>
        <Box sx={{ flex: 1, flexBasis: 0 }} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            px: 2,
          }}>
          {logoUrl && (
            <Box
              component='img'
              src={logoUrl}
              alt='Client Logo'
              sx={{
                maxHeight: '38px',
                maxWidth: '200px',
                objectFit: 'contain',
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            flexBasis: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            minWidth: 0,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <IconButton
              onClick={handleCloseWindow}
              sx={{
                color: '#333',
                backgroundColor: 'rgba(0,0,0,0.05)',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' },
              }}
              title='Close Fullscreen'>
              <CloseFullscreenIcon sx={{ fontSize: '28px' }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          pb: 12,
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            mt: 4,
            mb: 8,
          }}>
          <Box sx={{ width: '10%', flexShrink: 0 }} />

          <Box sx={{ width: '45%', flexShrink: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'max-content 1fr',
                columnGap: 4,
                rowGap: 0.5,
                alignItems: 'start',
              }}>
              <InfoBlock label='Date' value={liveMarker.date || liveMarker.Date} />
              <InfoBlock label='Showcase' value={showcaseValue} />
              <InfoBlock label='Hilight' value={hilightValue} />
              <InfoBlock
                label='Location ID'
                value={
                  liveMarker.locationID || liveMarker.locationId || liveMarker.LocationID
                }
              />
              <InfoBlock
                label='Title'
                value={liveMarker.title || liveMarker.name || liveMarker.Title}
              />
              <InfoBlock
                label='Web Site'
                value={liveMarker.website || liveMarker.webSite || liveMarker.Website}
              />
              <InfoBlock label='Lat' value={liveMarker.lat || liveMarker.Lat} />
              <InfoBlock label='Lng' value={liveMarker.lng || liveMarker.Lng} />
              <InfoBlock
                label='Location Address'
                value={
                  liveMarker.locationAddress || liveMarker.address || liveMarker.Address
                }
              />
              <InfoBlock label='City' value={liveMarker.city || liveMarker.City} />
              <InfoBlock label='State' value={liveMarker.state || liveMarker.State} />
              <InfoBlock
                label='PostCode / Zip'
                value={
                  liveMarker.postCode ||
                  liveMarker.postcode ||
                  liveMarker.zip ||
                  liveMarker.PostCode
                }
              />
              <InfoBlock
                label='Country'
                value={liveMarker.country || liveMarker.Country}
              />
              <InfoBlock
                label='Client Name'
                value={
                  liveMarker.clientName || liveMarker.ClientName || liveMarker.client
                }
              />
              <InfoBlock
                label='Category'
                value={
                  resolvedCategory ||
                  liveMarker.category ||
                  liveMarker.Category ||
                  liveMarker.categoryName ||
                  liveMarker.categories
                }
              />
              <InfoBlock
                label='Region'
                value={
                  resolvedRegion ||
                  liveMarker.region ||
                  liveMarker.Region ||
                  liveMarker.regionName
                }
              />
              <InfoBlock
                label='Controller'
                value={liveMarker.controller || liveMarker.Controller}
              />
              <InfoBlock
                label='Keywords'
                value={liveMarker.keywords || liveMarker.Keywords}
              />
            </Box>
          </Box>

          <Box sx={{ width: '5%', flexShrink: 0 }} />

          <Box sx={{ width: '30%', flexShrink: 0 }}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 2 }}>
              <IconButton
                onClick={handleDownload}
                disabled={selectedDocIds.size === 0}
                sx={{ p: 0 }}
                title='Download Selected'>
                <MenuDownload
                  sx={{
                    fontSize: 54,
                    opacity: selectedDocIds.size === 0 ? 0.35 : 1,
                    filter: selectedDocIds.size === 0 ? 'grayscale(100%)' : 'none',
                  }}
                  iconScale={5}
                  iconX={32}
                  iconY={38}
                  textY={108}
                />
              </IconButton>
            </Box>

            <Typography
              sx={{
                fontWeight: 'bold',
                color: '#00008B',
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                mb: 1,
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}>
              Documents
            </Typography>

            {docs.length === 0 ? (
              <Typography
                sx={{ color: 'text.secondary', fontSize: '0.9rem', fontStyle: 'italic' }}>
                No documents available.
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ backgroundColor: 'transparent' }}>
                <Table
                  size='small'
                  sx={{ borderCollapse: 'separate', borderSpacing: '0 4px' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        padding='checkbox'
                        sx={{ width: '40px', borderBottom: 'none', pb: 0 }}>
                        <Checkbox
                          indeterminate={isDocsIndeterminate}
                          checked={isAllDocsSelected}
                          onChange={handleSelectAllDocs}
                          size='small'
                          sx={{ padding: 0, '&.Mui-checked': { color: '#00008B' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none', pb: 0, pl: 1 }}>
                        <Typography
                          sx={{
                            color: '#00008B',
                            fontWeight: 'bold',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                          }}>
                          DOCUMENT NAME
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {docs.map((row) => {
                      const isSelected = selectedDocIds.has(row.id);
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={(event) => handleDocRowClick(event, row.id)}
                          selected={isSelected}
                          sx={{ cursor: 'pointer', '& td': { borderBottom: 'none' } }}>
                          <TableCell padding='none'>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                pl: '4px',
                              }}>
                              <Box
                                sx={{
                                  borderRadius: '4px',
                                  width: '30px',
                                  height: '30px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#fff',
                                  border: '1px solid orange',
                                }}>
                                <Checkbox
                                  checked={isSelected}
                                  size='small'
                                  sx={{
                                    padding: 0,
                                    '&.Mui-checked': { color: '#00008B' },
                                  }}
                                />
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ pl: 1 }}>
                            <Box
                              sx={{
                                borderRadius: '4px',
                                minHeight: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px 8px',
                                backgroundColor: '#fff',
                                border: '1px solid orange',
                                width: '100%',
                              }}>
                              <Typography
                                sx={{
                                  fontSize: '0.8rem',
                                  color: '#333',
                                  fontFamily:
                                    '"Roboto", "Helvetica", "Arial", sans-serif',
                                }}
                                noWrap>
                                {row.fileName || 'Untitled'}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          <Box sx={{ width: '10%', flexShrink: 0 }} />
        </Box>

        {displaySlides.map((slide) => {
          const isPhotoLeft = slide.originalIndex % 2 === 0;
          const hasRichText =
            slide.description &&
            slide.description.trim() !== '' &&
            slide.description !== '<p></p>';

          return (
            <Box
              key={`${slide.id}-${slide.originalIndex}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                width: '100%',
                mb: 8,
              }}>
              {isPhotoLeft ? (
                <>
                  <Box sx={{ width: '10%', flexShrink: 0 }} />

                  <Box
                    sx={{
                      width: '45%',
                      flexShrink: 0,
                      aspectRatio: '1 / 1',
                      backgroundColor: '#f9f9f9',
                      overflow: 'hidden',
                      borderRadius: '4px',
                    }}>
                    <MediaElement slide={slide} />
                  </Box>

                  <Box sx={{ width: '5%', flexShrink: 0 }} />

                  <Box sx={{ width: '30%', flexShrink: 0, mt: '60px' }}>
                    {hasRichText && (
                      <Box
                        sx={{
                          typography: 'body1',
                          color: 'text.secondary',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          textTransform: 'none !important', // AMENDED: Prevent global theme uppercase leakage
                          '& p': { mt: 0, mb: '6pt', lineHeight: 1.2 }, // AMENDED: Sync with RTE margins
                          '& p:last-of-type': { mb: 0 },
                          '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '4px',
                          },
                          '& ul, & ol': { paddingLeft: '1.5rem', mt: 0, mb: 0 },
                          '& li': { marginTop: 0, marginBottom: '6pt', lineHeight: 1.1 },
                          '& li > p': { margin: '0 !important' },
                          '& a': { color: '#1976d2', textDecoration: 'none' },
                        }}
                        dangerouslySetInnerHTML={{ __html: slide.description }}
                      />
                    )}
                  </Box>

                  <Box sx={{ width: '10%', flexShrink: 0 }} />
                </>
              ) : (
                <>
                  <Box sx={{ width: '10%', flexShrink: 0 }} />

                  <Box sx={{ width: '30%', flexShrink: 0, mt: '60px' }}>
                    {hasRichText && (
                      <Box
                        sx={{
                          typography: 'body1',
                          color: 'text.secondary',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          textTransform: 'none !important', // AMENDED: Prevent global theme uppercase leakage
                          '& p': { mt: 0, mb: '6pt', lineHeight: 1.2 }, // AMENDED: Sync with RTE margins
                          '& p:last-of-type': { mb: 0 },
                          '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '4px',
                          },
                          '& ul, & ol': { paddingLeft: '1.5rem', mt: 0, mb: 0 },
                          '& li': { marginTop: 0, marginBottom: '6pt', lineHeight: 1.1 },
                          '& li > p': { margin: '0 !important' },
                          '& a': { color: '#1976d2', textDecoration: 'none' },
                        }}
                        dangerouslySetInnerHTML={{ __html: slide.description }}
                      />
                    )}
                  </Box>

                  <Box sx={{ width: '5%', flexShrink: 0 }} />

                  <Box
                    sx={{
                      width: '45%',
                      flexShrink: 0,
                      aspectRatio: '1 / 1',
                      backgroundColor: '#f9f9f9',
                      overflow: 'hidden',
                      borderRadius: '4px',
                    }}>
                    <MediaElement slide={slide} />
                  </Box>

                  <Box sx={{ width: '10%', flexShrink: 0 }} />
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
